import type { Role } from '../lib/auth';

/**
 * "How this works" for every staff screen, in plain words for people who are not technical.
 *
 * Each entry has 3–5 short lines, always in this order:
 *   1. what the page is for (also the one line shown for it on "What I can do"),
 *   2. what you can do here,
 *   3. what updates live (by itself, without reloading),
 *   4. one tip.
 *
 * Where a page means something different to different people (a shop admin, a counter login and the super
 * admin all open "Bills"), `roles` replaces the fields that differ. Keys are a role, or a group:
 * 'shop' (shop admin and counter logins) and 'venueWide' (the super admin and the accountant).
 * A role's own entry wins over its group's.
 */
export interface Help {
  title: string;
  /** Who uses this page, in a few words. */
  forWhom: string;
  lines: string[];
}

export type Audience = Role | 'shop' | 'venueWide';

export interface HelpDef extends Help {
  /** The route under /admin as written in App.tsx ('' for /admin itself), e.g. 'visits/:id'. */
  path: string;
  roles?: Partial<Record<Audience, Partial<Help>>>;
  /** Opens on its own (a printable page or the sign-in page), without the menu and the help button. */
  standalone?: boolean;
}

const LIVE_SELF = 'This page updates by itself when something changes (watch the green dot at the top).';
const SAVED_AT_ONCE = 'What you save here applies at once; others see it the next time their screen refreshes.';

export const HELP: HelpDef[] = [
  // ------------------------------------------------------------------ overview
  {
    path: 'dashboard',
    title: 'Dashboard',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      "Today's numbers for the whole venue at a glance: parking, shops, money in.",
      'Look at the cards and the daily trend; tap a card to open the page behind it.',
      LIVE_SELF,
      'Start the day here, then open the page you need from the menu.',
    ],
    roles: {
      cashier: {
        forWhom: 'The cashier',
        lines: [
          "Today's collections and vehicles at your gate at a glance.",
          'Check how much has come in and how many vehicles are inside or waiting to pay.',
          LIVE_SELF,
          'To take a payment, open Collect payment from the menu.',
        ],
      },
    },
  },
  {
    path: 'shops',
    title: 'All shops',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Profit and loss of every shop and outlet side by side for the period you pick.',
      'Pick a period, sort the list, download Excel, or tap a shop to open it.',
      'New bills and expenses change the figures by themselves.',
      'Shops shown in red made a loss in this period. Tap "Open shop" to see why.',
    ],
  },
  {
    path: 'shops/daily',
    title: 'Daily sales by shop',
    forWhom: 'The super admin and the accountant',
    lines: [
      'What every shop sold, day by day: bills, items sold, sales, returns, net and profit.',
      'Pick Today, Yesterday, 7 days or a month; switch between By day, By shop and Products sold.',
      'Tap a day cell to see that shop\'s bills, payment modes and top products for that day.',
      'Figures match each shop\'s profit & loss to the paisa and update as bills come in.',
      'Download CSV or Excel of the tab you are on.',
    ],
  },
  {
    path: 'live-activity',
    title: 'Live activity',
    forWhom: 'Everyone',
    lines: [
      'A running list of what is happening right now: entries, exits, bills, payments.',
      'Scroll the list or filter it; tap an item to open it.',
      'New items appear at the top by themselves. You only see what your login may see.',
      'If the dot at the top is grey, the list still refreshes every few seconds.',
    ],
    roles: {
      shop: {
        forWhom: 'Your shop team',
        lines: [
          "A running list of what is happening in your shop: bills, returns, stock changes.",
          'Scroll the list; tap a bill to open it.',
          'New items appear at the top by themselves. Other shops are never shown here.',
          'Keep this open on a second screen to watch a busy counter.',
        ],
      },
    },
  },
  {
    path: 'simulate',
    title: 'Simulate operations',
    forWhom: 'The super admin (test servers only)',
    lines: [
      'Makes pretend traffic (vehicles, bills, payments) so you can see the app working.',
      'Start or stop a simulation and choose how busy it should be.',
      'Every other screen updates live while it runs, just like a real day.',
      'Use "See it as each user" to open the app as a shop admin or counter.',
    ],
  },

  // ------------------------------------------------------------------ parking
  {
    path: 'gate',
    title: 'Gate & vehicles inside',
    forWhom: 'Gate security and the super admin',
    lines: [
      'Record every vehicle that comes in and goes out at this gate.',
      'Type or scan the number plate, add passengers, then tap Record entry. Tap Exit when it leaves.',
      'The list of vehicles inside updates by itself, and at least every 30 seconds.',
      'If the plate is already inside, the page tells you and links to that visit.',
    ],
  },
  {
    path: 'visitors',
    title: 'Visitors & passengers',
    forWhom: 'Gate staff and the super admin',
    lines: [
      'How many people (PAX) came in, counted at the gate, and the groups in each vehicle.',
      'Pick a day or a range to see totals by hour and by vehicle.',
      'Counts change by themselves as vehicles come in.',
      'Ask the gate to fill in passengers at entry, or this page stays empty.',
    ],
  },
  {
    path: 'live',
    title: 'Live park status',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      'How full the parking is right now, zone by zone.',
      'See vehicles inside, free space and overstays.',
      LIVE_SELF,
      'Overstays are vehicles parked longer than the allowed time.',
    ],
  },
  {
    path: 'collect',
    title: 'Collect payment',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Take the parking fee from vehicles that have exited.',
      'Pick the vehicle, choose cash, UPI, card or wallet, then confirm. Print the slip if asked.',
      'Vehicles waiting to pay appear by themselves when the gate records an exit.',
      'If the exit is not recorded yet, the page lets you record it first.',
    ],
  },
  {
    path: 'visits',
    title: 'Visit history',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'Every vehicle entry, searchable by number plate.',
      'Search a plate or filter by date and status; tap a visit to see its details.',
      'New visits are added by themselves.',
      'Type only part of the plate (e.g. 1234) to find it quickly.',
    ],
  },
  {
    path: 'visits/:id',
    title: 'Visit details',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'One vehicle visit: when it came in and out, the fee, photos and payments.',
      'Reprint the entry pass, record the exit or open the payment.',
      'Payments and the exit appear by themselves when someone records them.',
      'Only the super admin can cancel a visit; the reason is kept in the audit log.',
    ],
  },
  {
    path: 'vehicles/:id',
    title: 'Vehicle',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'One vehicle and all its visits to the venue.',
      'See how often it came, what it paid and which customer it belongs to.',
      'New visits of this vehicle show up by themselves.',
      'Link the vehicle to a customer so their app shows their visits.',
    ],
  },
  {
    path: 'printer',
    title: 'Printer',
    forWhom: 'Counters and the gate',
    lines: [
      'Connect the receipt printer used on this device.',
      'Pick the printer type and paper size, connect it, then print a test receipt.',
      'The status shows straight away if the printer disconnects.',
      'This setting is kept on this device only; set it on each counter phone or PC.',
    ],
    roles: {
      shop: {
        forWhom: 'Your shop counter',
        lines: [
          'Connect the receipt printer used for bills on this device.',
          'Pick the printer type and paper size, connect it, then print a test receipt.',
          'The status shows straight away if the printer disconnects.',
          'Set it once on each counter phone or PC; it is kept on that device.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ shops (the venue's view)
  {
    path: 'outlets',
    title: 'Shops & outlets',
    forWhom: 'The super admin',
    lines: [
      'Every parking gate and shop at the venue, with its admin login.',
      'Add a shop (or upload many at once), edit its details, or switch it off.',
      SAVED_AT_ONCE,
      'Each shop admin sees only their own shop, never another one.',
    ],
  },

  // ------------------------------------------------------------------ a shop's own pages
  {
    path: 'shop-home',
    title: 'Shop home',
    forWhom: 'Shop admins and counters',
    lines: [
      "Your shop's day at a glance: sales so far, bills, items running low.",
      'Start billing, add an expense, close the day or add a product from here.',
      'Figures change by themselves as bills are made.',
      'Check "Running low" before the evening rush.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          'Your counter at a glance: your bills today and items running low.',
          'Tap Start billing to make a bill, or Reprint to print one of today\'s bills again.',
          'Your bills and stock change by themselves.',
          'Ask your shop admin if you need to see more (for example the shop\'s takings).',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's day at a glance: sales, bills and items running low.",
          'Look only: the shop admin runs the shop. Use "← All shops" to go back.',
          'Figures change by themselves as the shop makes bills.',
          'Use Profit & loss in the menu for this shop\'s full figures.',
        ],
      },
    },
  },
  {
    path: 'pos',
    title: 'Billing',
    forWhom: 'Shop counters and shop admins',
    lines: [
      'Make a bill for a customer.',
      'Tap items (or scan a code), change quantities, add a customer if they want, then take payment.',
      'Prices and stock update by themselves when the shop admin changes them.',
      'Connect a printer under Printer to print bills.',
    ],
    roles: {
      owner: {
        forWhom: 'The super admin at a venue-run counter',
        lines: [
          'Make a bill at this venue-run counter.',
          'Tap items, change quantities, then take payment.',
          'Prices and stock update by themselves.',
          'Switch on "Work at this counter" in the shop bar to bill here.',
        ],
      },
    },
  },
  {
    path: 'bills',
    title: 'Bills',
    forWhom: 'Shop admins, the super admin and the accountant',
    lines: [
      "Every bill your shop made, with the day's totals.",
      'Filter by date, status or cashier, search a bill or customer, download CSV or Excel.',
      'New bills appear by themselves.',
      'Tap a bill to reprint it or to take a return.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          'The bills you made, starting with today.',
          'Search a bill or customer; pick "Everyone" to see the whole counter if allowed.',
          'New bills appear by themselves.',
          'Tap a bill to reprint it for the customer.',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          'Bills of every shop, or of one shop you pick.',
          'Filter by shop, date, status or cashier and download CSV or Excel.',
          'New bills appear by themselves.',
          'Pick one shop in the Shop box to see only its bills.',
        ],
      },
    },
  },
  {
    path: 'bills/:id',
    title: 'Bill',
    forWhom: 'Shop teams, the super admin and the accountant',
    lines: [
      'One bill: items, taxes, payments and returns.',
      'Reprint the bill, or take a return or refund if you are allowed.',
      'Returns made on another counter appear here by themselves.',
      'A return never deletes the bill; it adds a return line instead.',
    ],
  },
  {
    path: 'products',
    title: 'Products & stock',
    forWhom: 'Shop admins',
    lines: [
      "Your shop's items, prices and stock.",
      'Add or edit products, record stock coming in, wastage or a count correction, import from a file.',
      'Stock goes down by itself as bills are made.',
      'Group items in categories so the counter finds them fast.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          "Your shop's items, prices and stock.",
          'Look up a price or how many are left. Changes need permission from your shop admin.',
          'Stock goes down by itself as bills are made.',
          'Tell your shop admin when something runs out.',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's items, prices and stock.",
          'Look at prices, stock and stock movements.',
          'Stock goes down by itself as the shop makes bills.',
          'Each shop keeps its own list; another shop never sees it.',
        ],
      },
    },
  },
  {
    path: 'shop-pnl',
    title: 'Profit & loss',
    forWhom: 'Shop admins, the super admin and the accountant',
    lines: [
      'How much your shop earned after costs and expenses for the period you pick.',
      'Pick a period, see sales, cost of goods, expenses and profit, download it.',
      'New bills and expenses change the figures by themselves.',
      'Add every expense under Expenses, or the profit will look higher than it is.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's profit and loss for the period you pick.",
          'Pick a period, see sales, costs, venue commission and profit, download it.',
          'New bills and expenses change the figures by themselves.',
          'Use "All shops" to compare it with the other shops.',
        ],
      },
    },
  },
  {
    path: 'shop-staff',
    title: 'Staff logins',
    forWhom: 'Shop admins',
    lines: [
      "Sign-in logins for your shop's counters and team.",
      'Add a login, choose what it may do (bill, give discounts, see takings…), reset a password or switch it off.',
      'A change applies the next time that person opens a page.',
      'Give each person their own login, so every bill shows who made it.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin',
        lines: [
          "This shop's logins and what each may do.",
          'Look at who can sign in; the shop admin manages them.',
          'A change applies the next time that person opens a page.',
          'To add or reset any login yourself, use Staff users.',
        ],
      },
    },
  },
  {
    path: 'shop-settings',
    title: 'Receipt & shop details',
    forWhom: 'Shop admins',
    lines: [
      "Your shop's name, address, phone, logo and what the printed bill looks like.",
      'Change the details, upload a logo and preview the receipt.',
      SAVED_AT_ONCE,
      'GSTIN and tax type are set by the super admin; ask them if they are wrong.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin',
        lines: [
          "This shop's details and receipt layout.",
          'Look at or correct the details printed on its bills.',
          SAVED_AT_ONCE,
          'GSTIN, tax type and commission are changed under Shops & outlets.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ activities
  {
    path: 'games',
    title: 'Games & rides',
    forWhom: 'The cashier and the super admin',
    lines: [
      'The attractions, plays sold and which ones are working.',
      'Sell a play, mark a ride out of order or back in service.',
      'Plays and faults appear by themselves.',
      'Mark a broken ride at once so the counter stops selling it.',
    ],
  },
  {
    path: 'turf',
    title: 'Turf booking',
    forWhom: 'The cashier and the super admin',
    lines: [
      'The turf calendar: who booked which slot.',
      'Book a slot, take the payment, or cancel a booking.',
      'Bookings made on another counter appear by themselves.',
      'A slot that is already booked cannot be booked twice.',
    ],
  },
  {
    path: 'events',
    title: 'Events',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Venue events with seats, tickets, money in and costs.',
      'Create an event, sell tickets and see how full it is.',
      'Ticket sales update by themselves.',
      'Set the capacity first; sales stop when it is full.',
    ],
  },

  // ------------------------------------------------------------------ commerce (venue charges)
  {
    path: 'settlements',
    title: 'Shop settlements',
    forWhom: 'The super admin and the accountant',
    lines: [
      "Each tenant shop's monthly statement: commission, rent, cleaning, generator and penalties.",
      'Prepare drafts for the month, check each one, confirm it, record the payment.',
      'Figures refresh by themselves while you work.',
      'Nothing is booked until you confirm a draft.',
    ],
    roles: {
      shop_admin: {
        title: 'Venue statements',
        forWhom: 'Shop admins of tenant shops',
        lines: [
          'The monthly statements the venue sends your shop: rent, commission and other charges.',
          'Open a statement, print it, and see charges still to come this month.',
          'A new statement appears here by itself when the venue confirms it.',
          'Questions about a charge? Ask the venue office (super admin).',
        ],
      },
    },
  },
  {
    path: 'agreements',
    title: 'Rentals & rent holding',
    forWhom: 'The super admin and the accountant',
    lines: [
      "Each tenant shop's agreement: rent, deposit, commission, power load and floor area.",
      'Add or edit an agreement and its dates.',
      SAVED_AT_ONCE,
      'The monthly statement uses the agreement that is active on that day.',
    ],
  },
  {
    path: 'closures',
    title: 'Closure register',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Days a shop stayed closed and the notice it gave.',
      'Record a closure, its notice, and waive a penalty if needed.',
      'New entries appear by themselves.',
      'Closures without enough notice add a penalty to the monthly statement.',
    ],
  },
  {
    path: 'advertisements',
    title: 'Advertisements',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Advertising space and screen time sold at the venue.',
      'Make a quote, turn it into an invoice, record the payment.',
      'Changes show up by themselves.',
      'Print the invoice from the advertisement to give to the client.',
    ],
  },
  {
    path: 'cleaning-charges',
    title: 'Cleaning charges',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Cleaning done for a shop, charged on its monthly statement.',
      'Add a charge, or stop a repeating one.',
      'New charges appear by themselves.',
      'Charges added after a statement is confirmed go on the next month.',
    ],
  },
  {
    path: 'generator',
    title: 'Generator & cost allocation',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Generator runs, their cost, and how it is split between shops.',
      'Record a run with hours and diesel; see each shop\'s share.',
      'New runs appear by themselves.',
      'Shares follow each shop\'s connected load from its agreement.',
    ],
  },

  // ------------------------------------------------------------------ facility
  {
    path: 'housekeeping',
    title: 'Housekeeping',
    forWhom: 'Housekeeping staff and supervisors',
    lines: [
      'The cleaning rota: what to clean, where and when.',
      'Tick off a task when it is done; supervisors check and sign it off.',
      'Tasks ticked on another phone show up by themselves.',
      'Add a photo when something needs a supervisor to look at it.',
    ],
  },
  {
    path: 'quality',
    title: 'Quality control',
    forWhom: 'Supervisors and the super admin',
    lines: [
      'Inspections of shops and areas with a checklist and a pass mark.',
      'Start an inspection, score each point, and see the latest score by shop.',
      'New inspections appear by themselves.',
      'Follow up quickly on anything that failed.',
    ],
  },
  {
    path: 'pest-control',
    title: 'Pest control',
    forWhom: 'Facility staff and the super admin',
    lines: [
      'Scheduled pest treatments across shops and common areas.',
      'Record a treatment and see what is due next, area by area.',
      'Updates appear by themselves.',
      'Overdue areas are shown first.',
    ],
  },
  {
    path: 'maintenance',
    title: 'Maintenance',
    forWhom: 'Maintenance staff and the super admin',
    lines: [
      'Breakdown and regular maintenance jobs across the park, game repairs included.',
      'Report a problem, assign it, and close it when fixed.',
      'New and updated jobs appear by themselves.',
      'Write what was done when you close a job, for the next person.',
    ],
  },
  {
    path: 'assets',
    title: 'Assets',
    forWhom: 'The super admin and facility managers',
    lines: [
      "The park's equipment with value, warranty, service dates and repairs.",
      'Add an asset, record a service or repair, see value by category.',
      'Changes show up by themselves.',
      'Fill in the warranty date so you are reminded before it ends.',
    ],
  },
  {
    path: 'other-services',
    title: 'Other services',
    forWhom: 'Facility staff and the super admin',
    lines: [
      'Chargeable services for shops, events and guests, and services bought from vendors.',
      'Record a service, who it was for and what it costs.',
      'New entries appear by themselves.',
      'Services for a shop can be added to its monthly statement.',
    ],
  },

  // ------------------------------------------------------------------ people
  {
    path: 'employees',
    title: 'Employees',
    forWhom: 'HR and the super admin',
    lines: [
      "The venue's own workers (shop staff are paid by their shop and are not here).",
      'Add or edit an employee, their department, salary and documents.',
      'Changes show up by themselves.',
      'Keep bank details filled in so payroll can be paid.',
    ],
  },
  {
    path: 'attendance',
    title: 'Attendance',
    forWhom: 'HR and supervisors',
    lines: [
      'Check staff in and out each day.',
      'Tap a person to check them in or out; hours, overtime and half days are worked out for you.',
      'Check-ins on another phone appear by themselves.',
      'Payroll uses these hours, so correct mistakes the same day.',
    ],
  },
  {
    path: 'payroll',
    title: 'Payroll',
    forWhom: 'HR, the accountant and the super admin',
    lines: [
      'Monthly salaries worked out from attendance, with PF, ESI, advances and room rent.',
      'Prepare the month, check each payslip, then someone else approves it.',
      'Figures refresh by themselves while you work.',
      'The person who prepares payroll cannot approve it.',
    ],
  },
  {
    path: 'hr-reminders',
    title: 'HR payment reminders',
    forWhom: 'HR, payroll and the super admin',
    lines: [
      'Salaries, PF and ESI, advances to recover and other staff payments as they fall due.',
      'See what is due soon and mark it paid.',
      'New reminders appear by themselves.',
      'Check this at the start of each week.',
    ],
  },
  {
    path: 'accommodation',
    title: 'Accommodation',
    forWhom: 'HR',
    lines: [
      'Staff rooms and dormitories, and who stays where.',
      'Check a person into a room or out of it.',
      'Changes show up by themselves.',
      'Rent is taken from salary for every night stayed.',
    ],
  },

  // ------------------------------------------------------------------ supply chain
  {
    path: 'materials',
    title: 'Materials & stock',
    forWhom: 'The store and the accountant',
    lines: [
      "The venue's store: what is in stock and what it is worth.",
      'Receive, issue and count materials.',
      'Stock changes on another device appear by themselves.',
      'Count the store regularly and record differences.',
    ],
  },
  {
    path: 'material-requests',
    title: 'Material requests',
    forWhom: 'Departments, the store and the accountant',
    lines: [
      'Ask the store for materials and follow the request.',
      'Raise a request, approve it, and issue it from the store.',
      'Status changes appear by themselves.',
      'If the store has none, turn the request into a purchase order.',
    ],
  },
  {
    path: 'procurement',
    title: 'Procurement',
    forWhom: 'The accountant and the super admin',
    lines: [
      'Buying from suppliers: request, approval, purchase order, goods received.',
      'Create a purchase order, record what arrived, then add the supplier bill.',
      'Status changes appear by themselves.',
      'Print the purchase order to send to the supplier.',
    ],
  },
  {
    path: 'supplier-bills',
    title: 'Supplier bills',
    forWhom: 'The accountant and the super admin',
    lines: [
      "Supplier bills to check, approve and pay.",
      "Enter the supplier's bill, approve it (that books the expense), then record the payment.",
      'New bills and payments appear by themselves.',
      'Match the bill with the goods received before approving it.',
    ],
  },

  // ------------------------------------------------------------------ money
  {
    path: 'payments',
    title: 'Payments',
    forWhom: 'The cashier, the accountant and the super admin',
    lines: [
      'Every collection, online payment and refund.',
      'Filter by date, type or status; tap a payment for details or a refund.',
      'New payments appear by themselves.',
      'Only the accountant and the super admin can refund.',
    ],
  },
  {
    path: 'payments/:id',
    title: 'Payment',
    forWhom: 'The cashier, the accountant and the super admin',
    lines: [
      'One payment: amount, method, reference and what it was for.',
      'Print the receipt or refund it if you are allowed.',
      'Its status updates by itself (for example when UPI confirms).',
      'A refund is a new entry; the original payment is never changed.',
    ],
  },
  {
    path: 'closing',
    title: 'Daily closing',
    forWhom: 'Cashiers, shop admins, the accountant and the super admin',
    lines: [
      "Count the money at the end of the day and close the day's books.",
      'Enter the cash counted and the UPI and card totals; the page shows any difference; then Close day.',
      'The expected amounts update by themselves until you close.',
      'After closing, only the super admin can reopen a day.',
    ],
    roles: {
      shop: {
        title: 'Close the day',
        forWhom: 'Shop admins',
        lines: [
          "Close your shop's day: count the cash and check the takings.",
          'Enter the cash in the drawer and the UPI and card totals, check the difference, then Close day.',
          'Expected amounts update by themselves as bills are made.',
          'Close the day after the last bill, before you leave.',
        ],
      },
    },
  },
  {
    path: 'finance',
    title: 'Income & expenses',
    forWhom: 'The accountant, the cashier and the super admin',
    lines: [
      "The venue's income and expenses outside parking and shop bills.",
      'Add income or an expense, put it in a group, edit or reverse an entry.',
      'New entries appear by themselves.',
      'Reverse a wrong entry instead of deleting it; both stay in the books.',
    ],
    roles: {
      shop: {
        title: 'Expenses',
        forWhom: 'Shop admins',
        lines: [
          "Your shop's expenses: rent you pay, salaries, gas, supplies.",
          'Add an expense, edit it, or reverse a wrong one.',
          'New entries appear by themselves.',
          'Every expense lowers your profit on Profit & loss, so add them all.',
        ],
      },
    },
  },
  {
    path: 'finance/groups',
    title: 'Account groups',
    forWhom: 'The accountant and the super admin',
    lines: [
      'Groups and subgroups used to sort income and expenses (e.g. Marketing › Social media).',
      'Add, rename or retire a group.',
      SAVED_AT_ONCE,
      'Keep the list short so everyone picks the same group.',
    ],
  },
  {
    path: 'reports',
    title: 'Reports',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      'Ready-made reports: collections, visits, shops and more.',
      'Pick a report and a period, then view or download it.',
      'Figures refresh by themselves while you look.',
      'Use Excel downloads for your accountant or auditor.',
    ],
  },

  // ------------------------------------------------------------------ customers
  {
    path: 'customers',
    title: 'Customers',
    forWhom: 'Gate staff, cashiers, the accountant and the super admin',
    lines: [
      'Find a customer by mobile number, name or vehicle number.',
      'Search, open a customer, or add a new one.',
      'New customers and changes appear by themselves.',
      'Searching by the last digits of the mobile is fastest.',
    ],
  },
  {
    path: 'customers/:id',
    title: 'Customer',
    forWhom: 'Gate staff, cashiers, the accountant and the super admin',
    lines: [
      'One customer: profile, vehicles, visits, wallet and coupons.',
      'Edit their details, link a vehicle, see payments, print a wallet statement.',
      'Wallet and visits update by themselves.',
      'Only change a mobile number when the customer asks you to.',
    ],
  },
  {
    path: 'customers/:id/statement',
    standalone: true,
    title: 'Wallet statement',
    forWhom: 'Cashiers and the super admin',
    lines: ["A printable statement of a customer's wallet.", 'Print it or save it as PDF.', 'It shows the wallet as it is when you open it.', 'Close it to go back.'],
  },
  {
    path: 'cards',
    title: 'Cards & recharge',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Wallet cards for customers: issue, recharge, block.',
      "Scan the customer's QR or card, recharge it, print the receipt or a statement.",
      'Balances update by themselves when the card is used.',
      'Block a lost card at once; its balance stays safe.',
    ],
  },
  {
    path: 'cards/receipts/:paymentId',
    standalone: true,
    title: 'Recharge receipt',
    forWhom: 'The cashier',
    lines: ['A printable receipt for a wallet recharge.', 'Print it for the customer.', 'It shows the recharge as recorded.', 'Close it to go back to Cards.'],
  },
  {
    path: 'coupons',
    title: 'Redeem coupon',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Check and use a coupon a customer shows you.',
      'Scan the coupon or type its code, then redeem it.',
      'A coupon used elsewhere is refused straight away.',
      'Each coupon works once; the page tells you if it is expired.',
    ],
  },
  {
    path: 'campaigns',
    title: 'Campaigns',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Spend-based coupon offers and promotions shown in the customer app.',
      'Create a campaign, set its rules and dates, pause or end it.',
      'Changes show up by themselves.',
      'Start with a short campaign to see how customers respond.',
    ],
  },
  {
    path: 'notifications',
    title: 'Notifications',
    forWhom: 'The super admin',
    lines: [
      'Offers and event news sent to customers in the app.',
      'Write a message, choose who gets it and when, then send or schedule it.',
      'Sending status updates by itself.',
      'Only customers who agreed to offers receive them.',
    ],
  },
  {
    path: 'feedback',
    title: 'Feedback',
    forWhom: 'Shop admins, the accountant and the super admin',
    lines: [
      'What guests say about the shops, the gate and the venue.',
      'Read comments, filter by shop or rating, and see the average rating.',
      'New feedback appears by itself.',
      'Reply quickly to low ratings.',
    ],
  },

  // ------------------------------------------------------------------ admin
  {
    path: 'users',
    title: 'Staff users',
    forWhom: 'The super admin',
    lines: [
      'Every login in the venue, grouped by shop.',
      'Add a login, choose its role and shops, reset a password, unlock or switch it off.',
      'Changes show up by themselves; the person gets them on their next page.',
      'Give each person their own login, never a shared one.',
    ],
  },
  {
    path: 'settings',
    title: 'Settings',
    forWhom: 'The super admin',
    lines: [
      'Business rules and switches for the whole venue.',
      'Turn features on or off and change rules.',
      'Changes apply at once and are written in the audit log.',
      'Change one thing at a time and check the result.',
    ],
  },
  {
    path: 'pricing',
    title: 'Pricing rules',
    forWhom: 'The super admin (the cashier can look)',
    lines: [
      'Parking charges per gate and vehicle type.',
      'See the active rule; the super admin can make a new version and switch it on.',
      'A new rule applies to vehicles from the moment it is switched on.',
      'Old versions are kept so past fees can always be explained.',
    ],
    roles: {
      cashier: {
        forWhom: 'The cashier',
        lines: [
          'The parking charges that apply today, per vehicle type.',
          'Look up the rate to answer a customer; only the super admin can change it.',
          'A change by the super admin shows here at once.',
          'The fee on Collect payment always follows this rule.',
        ],
      },
    },
  },
  {
    path: 'cameras',
    title: 'Cameras & number plates',
    forWhom: 'The super admin',
    lines: [
      'Gate cameras that read number plates and record entries and exits by themselves.',
      'Add a camera, test it, and see what it read.',
      'New reads appear by themselves; unclear ones go to the gate to confirm.',
      'Check the camera angle if many reads need confirming.',
    ],
  },
  {
    path: 'areas',
    title: 'Vehicle areas (RTO)',
    forWhom: 'The super admin',
    lines: [
      'Where visiting vehicles come from, from the RTO code on the plate (KL 07 = Ernakulam).',
      'Pick a period to see visits by district and state.',
      'Figures refresh by themselves while you look.',
      'Use it to plan where to advertise.',
    ],
  },
  {
    path: 'imports',
    title: 'Data import',
    forWhom: 'The super admin',
    lines: [
      'Bring in existing customers and vehicles from a spreadsheet.',
      'Upload a file, match its columns, check the rows, then import.',
      'Progress updates by itself while a file is processed.',
      'Try a small file first.',
    ],
  },
  {
    path: 'imports/:id',
    title: 'Import',
    forWhom: 'The super admin',
    lines: [
      'One uploaded file: its columns, rows and problems.',
      'Fix the column matching, check rows with problems, then import or discard.',
      'Progress updates by itself.',
      'Nothing is added until you press import.',
    ],
  },
  {
    path: 'terms',
    title: 'Terms & conditions',
    forWhom: 'The super admin',
    lines: [
      'The rules customers read and accept in the app, also shown on receipts.',
      'Write a new version and publish it.',
      'Customers see the new version the next time they open the app.',
      'Old versions are kept with who accepted them.',
    ],
  },
  {
    path: 'audit',
    title: 'Audit log',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Every sensitive change: who did it, when and what changed.',
      'Search by person, date or kind of change.',
      'New entries appear by themselves.',
      'Entries can never be edited or deleted.',
    ],
  },

  // ------------------------------------------------------------------ everyone
  {
    path: 'account',
    title: 'My account',
    forWhom: 'Everyone',
    lines: [
      'Your own login: name, role and password.',
      'Change your password here.',
      'Changing it signs you out on your other devices.',
      'Use a password only you know, and never share your login.',
    ],
  },
  {
    path: 'guide',
    title: 'What I can do',
    forWhom: 'Everyone',
    lines: [
      'Your menu explained in one line each, and who to ask for more access.',
      'Tap any item to open it.',
      'The list follows what your login may do right now.',
      'Every page also has a ? button at the top with its own short help.',
    ],
  },

  // ------------------------------------------------------------------ printable pages (no menu)
  { path: 'login', standalone: true, title: 'Sign in', forWhom: 'Everyone', lines: ['Sign in with the login and password you were given.', 'Type your login and password, then Sign in.', 'Nothing here updates by itself.', 'Logins are not case-sensitive and spaces are ignored.'] },
  { path: 'visits/:id/pass', standalone: true, title: 'Entry pass', forWhom: 'Gate staff', lines: ['A printable entry pass for one vehicle.', 'Print it and hand it to the driver.', 'It shows the visit as recorded.', 'Close it to go back to the gate.'] },
  { path: 'bills/:id/print', standalone: true, title: 'Print bill', forWhom: 'Shop teams', lines: ['A printable copy of one bill.', 'Print it or save it as PDF.', 'It shows the bill as it is now, returns included.', 'Close it to go back.'] },
  { path: 'settlements/:id/print', standalone: true, title: 'Statement', forWhom: 'The venue office and shop admins', lines: ['A printable monthly statement.', 'Print it or save it as PDF.', 'It shows the statement as confirmed.', 'Close it to go back.'] },
  { path: 'advertisements/:id/invoice', standalone: true, title: 'Advertisement invoice', forWhom: 'The venue office', lines: ['A printable invoice for an advertisement.', 'Print it or save it as PDF for the client.', 'It shows the invoice as recorded.', 'Close it to go back.'] },
  { path: 'purchase-orders/:id/print', standalone: true, title: 'Purchase order', forWhom: 'The venue office', lines: ['A printable purchase order.', 'Print it or save it as PDF to send to the supplier.', 'It shows the order as recorded.', 'Close it to go back.'] },
  { path: 'supplier-bills/:id/print', standalone: true, title: 'Supplier bill', forWhom: 'The venue office', lines: ['A printable supplier bill with its payments.', 'Print it or save it as PDF.', 'It shows the bill as recorded.', 'Close it to go back.'] },
];
